import React, { useState, useEffect } from "react";
import Select from "react-select";

export default function AddEditCheckpointModal({
  show,
  onClose,
  onSave,
  checkpoint,
  locations,
  isLoading,
}) {
  const [formData, setFormData] = useState({
    name: "",
    type: "QR",
    locationID: "",
    code: "",
    longitude: "",
    latitude: "",
    error_range: "",
    geofenceRadius: 100,
    note: "",
  });
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [codeError, setCodeError] = useState("");

  useEffect(() => {
    if (checkpoint) {
      // Edit mode
      setFormData({
        name: checkpoint.name || "",
        type: checkpoint.type || "QR",
        locationID: checkpoint.locationID?._id || checkpoint.locationID || "",
        code: checkpoint.code || "",
        longitude: checkpoint.longitude || "",
        latitude: checkpoint.latitude || "",
        error_range: checkpoint.error_range || "",
        geofenceRadius: checkpoint.geofenceRadius || 100,
        note: checkpoint.note || "",
      });
      const location = locations.find(
        (loc) => loc._id === (checkpoint.locationID?._id || checkpoint.locationID)
      );
      if (location) {
        setSelectedLocation({ _id: location._id, name: location.name });
      }
    } else {
      // Add mode
      setFormData({
        name: "",
        type: "QR",
        locationID: "",
        code: "",
        longitude: "",
        latitude: "",
        error_range: "",
        geofenceRadius: 100,
        note: "",
      });
      setSelectedLocation(null);
    }
    setCodeError("");
  }, [checkpoint, locations]);

  // Validate NFC code format
  const validateNFCCode = (code) => {
    const nfcCodePattern = /^([A-Fa-f0-9]{2}:){6}[A-Fa-f0-9]{2}$/;
    return nfcCodePattern.test(code);
  };

  const handleCodeChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, code: value });
    
    if (formData.type === "NFC" && value) {
      if (!validateNFCCode(value)) {
        setCodeError("NFC code must be in hexadecimal format (XX:XX:XX:XX:XX:XX:XX)");
      } else {
        setCodeError("");
      }
    } else {
      setCodeError("");
    }
  };

  const handleTypeChange = (e) => {
    const newType = e.target.value;
    setFormData({ 
      ...formData, 
      type: newType,
      code: newType === "QR" ? "" : formData.code // Clear code when switching to QR
    });
    setCodeError("");
  };

  const locationOptions = locations.map((loc) => ({
    _id: loc._id,
    name: loc.name,
    label: loc.name,
    value: loc._id,
  }));

  const handleSubmit = () => {
    if (!formData.name || !formData.locationID) {
      return;
    }
    
    // Validate NFC code if type is NFC
    if (formData.type === "NFC") {
      if (!formData.code) {
        setCodeError("NFC code is required for NFC checkpoints");
        return;
      }
      if (!validateNFCCode(formData.code)) {
        setCodeError("NFC code must be in hexadecimal format (XX:XX:XX:XX:XX:XX:XX)");
        return;
      }
    }
    
    // Don't send code for QR checkpoints (it's auto-generated) or GPS checkpoints (they don't use codes)
    const dataToSave = { ...formData };
    if (dataToSave.type === "QR" || dataToSave.type === "GPS") {
      delete dataToSave.code;
    }
    
    onSave(dataToSave);
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-700">
            {checkpoint ? "Edit Checkpoint" : "Add Checkpoint"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <i className="fa fa-times"></i>
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
              placeholder="Checkpoint name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.type}
              onChange={handleTypeChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              <option value="QR">QR Code</option>
              <option value="NFC">NFC</option>
              <option value="GPS">GPS Location</option>
            </select>
          </div>

          {formData.type === "NFC" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                NFC Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.code}
                onChange={handleCodeChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                  codeError
                    ? "border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:ring-gray-500"
                }`}
                placeholder="XX:XX:XX:XX:XX:XX:XX"
                maxLength={20}
              />
              {codeError && (
                <p className="text-red-500 text-xs mt-1">{codeError}</p>
              )}
              {!codeError && formData.code && (
                <p className="text-gray-500 text-xs mt-1">
                  Format: Hexadecimal (e.g., A1:B2:C3:D4:E5:F6:G7)
                </p>
              )}
            </div>
          )}

          {formData.type === "QR" && checkpoint && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                QR Code
              </label>
              <input
                type="text"
                value={checkpoint.code || ""}
                disabled
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed"
              />
              <p className="text-gray-500 text-xs mt-1">
                QR codes are auto-generated (UUID format) and cannot be changed
              </p>
            </div>
          )}
          
          {formData.type === "QR" && !checkpoint && (
            <div className="bg-blue-50 p-3 rounded-md">
              <p className="text-sm text-blue-800">
                <i className="fa fa-info-circle mr-2"></i>
                QR code will be auto-generated as a UUID when you create this checkpoint
              </p>
            </div>
          )}

          {formData.type === "GPS" && (
            <div className="bg-green-50 p-3 rounded-md">
              <p className="text-sm text-green-800">
                <i className="fa fa-info-circle mr-2"></i>
                GPS location checkpoints don't require a code. They use GPS coordinates for tracking.
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Location <span className="text-red-500">*</span>
            </label>
            <Select
              name="location"
              placeholder="Select Location"
              options={locationOptions}
              value={selectedLocation}
              onChange={(option) => {
                setSelectedLocation(option);
                setFormData({ ...formData, locationID: option ? option._id : "" });
              }}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option._id}
              isClearable={false}
              styles={{
                control: (provided) => ({
                  ...provided,
                  borderColor: "#d1d5db",
                  boxShadow: "none",
                  "&:hover": { borderColor: "#9ca3af" },
                }),
              }}
            />
          </div>

        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={
              isLoading || 
              !formData.name || 
              !formData.locationID || 
              (formData.type === "NFC" && !formData.code) ||
              (formData.type === "NFC" && !!codeError)
            }
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {checkpoint ? "Update" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

